import { Link } from 'react-router-dom';

/**
 * Forecaster Logo - Premium brand mark
 *
 * The Universal Node: A 3D cube representing the intelligence layer
 * that connects all infrastructure layers.
 *
 * Colors:
 * - Volt (lime-500) as the signature accent
 * - Cyan for runtime/infrastructure
 * - Violet for intelligence/ML
 */
export const ForecasterLogo = ({
    className = "h-8",
    clickable = true,
    size = "default"
}: {
    className?: string;
    clickable?: boolean;
    size?: "sm" | "default" | "lg"
}) => {
    const content = (
        <>
            {/* Logo Mark: The Universal Node */}
            <div className={`relative flex items-center justify-center ${
                size === "sm" ? "w-6 h-6" : size === "lg" ? "w-12 h-12" : "w-10 h-10"
            }`}>
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-volt-500/30 to-runtime-500/30 rounded-lg blur-lg animate-pulse-slow" />

                <svg
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-full h-full relative z-10 drop-shadow-[0_0_8px_rgba(132,204,22,0.5)]"
                >
                    {/* Main cube outline */}
                    <path
                        d="M20 4L4 12V28L20 36L36 28V12L20 4Z"
                        stroke="url(#logo-gradient)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* Internal lines */}
                    <path
                        d="M20 4V20M20 20L36 12M20 20L4 12"
                        stroke="url(#logo-gradient)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.5"
                    />
                    <path
                        d="M20 20L20 36"
                        stroke="url(#logo-gradient)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.5"
                    />
                    {/* Corner nodes */}
                    <circle cx="20" cy="4" r="2" fill="#84cc16" /> {/* volt-500 */}
                    <circle cx="36" cy="12" r="2" fill="#06b6d4" /> {/* runtime/cyan */}
                    <circle cx="4" cy="12" r="2" fill="#8b5cf6" /> {/* intel/violet */}
                    {/* Center node */}
                    <circle cx="20" cy="20" r="3" fill="white" />

                    <defs>
                        <linearGradient id="logo-gradient" x1="4" y1="4" x2="36" y2="36" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#84cc16" /> {/* volt-500 */}
                            <stop offset="0.5" stopColor="#22c55e" /> {/* econ-500 */}
                            <stop offset="1" stopColor="#06b6d4" /> {/* runtime-500 */}
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* Logotype */}
            <div className={`font-extrabold tracking-tight flex flex-col leading-none ${
                size === "sm" ? "text-sm" : size === "lg" ? "text-3xl" : "text-2xl"
            }`}>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-gray-300 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                    FORECASTER
                </span>
            </div>
        </>
    );

    if (clickable) {
        return (
            <Link
                to="/"
                className={`flex items-center gap-3 ${className} cursor-pointer hover:opacity-80 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt-500 rounded-md`}
                aria-label="Forecaster home"
            >
                {content}
            </Link>
        );
    }

    return <div className={`flex items-center gap-3 ${className}`}>{content}</div>;
};
